import React, { useState, useEffect, useMemo } from "react";
import { DatePicker } from "antd";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Card,
  CardContent,
  Stack,
  Button,
  Popover,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  getTicketStatusReport,
  getTicketPriorityReport,
  getTicketChannelReport,
  getSlaReport,
  getDepartmentReport,
  getCategoryReport,
} from "../../services/report.service";
import { getTicket } from "../../services/ticket.service";
import {
  AssignmentTurnedIn,
  AccessTime,
  ErrorOutline,
} from "@mui/icons-material";
import DepartmentReport from "../../components/DepartmentReport/DepartmentReport";
import DepartmentDatePicker from "../../components/DepartmentReport/DepartmentDatePicker";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const { RangePicker } = DatePicker;

const ReportCategory = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));

  const [departmentStartDate, setDepartmentStartDate] = useState(
    dayjs().startOf("month")
  );
  const [departmentEndDate, setDepartmentEndDate] = useState(
    dayjs().endOf("month")
  );

  const [ticketStatusData, setTicketStatusData] = useState({});
  const [loadingTicketStatus, setLoadingTicketStatus] = useState(false);

  const [ticketPriorityData, setTicketPriorityData] = useState({});
  const [loadingTicketPriority, setLoadingTicketPriority] = useState(false);

  const [ticketChannelData, setTicketChannelData] = useState([]);
  const [loadingTicketChannel, setLoadingTicketChannel] = useState(false);

  const [slaReportData, setSlaReportData] = useState([]);
  const [loadingSlaReport, setLoadingSlaReport] = useState(false);

  const [departmentReportData, setDepartmentReportData] = useState([]);

  const [loadingDepartmentReport, setLoadingDepartmentReport] = useState(false);

  const [categoryReportData, setCategoryReportData] = useState([]);
  const [loadingCategoryReport, setLoadingCategoryReport] = useState(false);

  const [categorySortKey, setCategorySortKey] = useState("closedWithout"); // openWithout | closedWithout | totalWithout | openWithin | closedWithin
  const [categorySortAsc, setCategorySortAsc] = useState(false); // false = descending (highest first)

  const [ticketChannelSortKey, setTicketChannelSortKey] = useState("closedWithout"); // openWithout | closedWithout | totalWithout | openWithin | closedWithin
  const [ticketChannelSortAsc, setTicketChannelSortAsc] = useState(false); // false = descending (highest first)

  // Process and sort category report data
  const categoryTableRows = useMemo(() => {
    if (!categoryReportData || typeof categoryReportData !== "object") {
      return [];
    }

    const baseRows = Object.entries(categoryReportData).map(([key, value]) => {
      // Debug logging
      console.log("Category:", key, "Value:", value);
      console.log("Open Within Tickets:", value.open?.withinTicketIds);
      console.log("Open Without Tickets:", value.open?.withoutTicketIds);
      console.log("Closed Within Tickets:", value.closed?.withinTicketIds);
      console.log("Closed Without Tickets:", value.closed?.withoutTicketIds);
      
      return {
        description: key,
        openWithin: value.open?.within || 0,
        openWithout: value.open?.without || 0,
        closedWithin: value.closed?.within || 0,
        closedWithout: value.closed?.without || 0,
        openWithinTickets: value.open?.withinTicketIds || [],
        openWithoutTickets: value.open?.withoutTicketIds || [],
        closedWithinTickets: value.closed?.withinTicketIds || [],
        closedWithoutTickets: value.closed?.withoutTicketIds || [],
        total:
          (value.open?.within || 0) +
          (value.open?.without || 0) +
          (value.closed?.within || 0) +
          (value.closed?.without || 0),
      };
    });

    const sortedRows = [...baseRows].sort((a, b) => {
      const totalWithoutA = (a.openWithout || 0) + (a.closedWithout || 0);
      const totalWithoutB = (b.openWithout || 0) + (b.closedWithout || 0);
      const openWithinA = a.openWithin || 0;
      const openWithinB = b.openWithin || 0;
      const closedWithinA = a.closedWithin || 0;
      const closedWithinB = b.closedWithin || 0;
      let va = 0;
      let vb = 0;
      if (categorySortKey === "openWithout") { va = a.openWithout || 0; vb = b.openWithout || 0; }
      else if (categorySortKey === "closedWithout") { va = a.closedWithout || 0; vb = b.closedWithout || 0; }
      else if (categorySortKey === "openWithin") { va = openWithinA; vb = openWithinB; }
      else if (categorySortKey === "closedWithin") { va = closedWithinA; vb = closedWithinB; }
      else { va = totalWithoutA; vb = totalWithoutB; }
      return categorySortAsc ? va - vb : vb - va;
    });

    return sortedRows;
  }, [categoryReportData, categorySortKey, categorySortAsc]);

  const handleCategorySort = (key) => {
    if (categorySortKey === key) {
      setCategorySortAsc((prev) => !prev);
    } else {
      setCategorySortKey(key);
      setCategorySortAsc(false);
    }
  };

  const handleTicketChannelSort = (key) => {
    if (ticketChannelSortKey === key) {
      setTicketChannelSortAsc((prev) => !prev);
    } else {
      setTicketChannelSortKey(key);
      setTicketChannelSortAsc(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    if (ticket && ticket._id) {
      try {
        // Fetch full ticket data using getTicket API
        // Search by ticketId for exact match
        const ticketIdToSearch = ticket.ticketId || ticket._id;
        const response = await getTicket({
          search: ticketIdToSearch,
          limit: 100, // Increase limit to ensure we find the ticket
        });
        
        // Find exact match by ticketId or _id
        let fullTicket = null;
        if (response?.data && response.data.length > 0) {
          // First try exact ticketId match
          fullTicket = response.data.find(
            (t) => t.ticketId === ticketIdToSearch
          );
          
          // If not found, try _id match (handle both string and ObjectId)
          if (!fullTicket) {
            fullTicket = response.data.find(
              (t) => {
                const tId = t._id?.toString();
                const ticketId = ticket._id?.toString();
                return tId === ticketId;
              }
            );
          }
          
          // Fallback to first result if exact match not found
          if (!fullTicket && response.data.length > 0) {
            fullTicket = response.data[0];
          }
        }
        
        if (fullTicket) {
          // Remove timer-related fields before navigation
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = fullTicket;
          console.log("Navigating with full ticket:", cleanTicket);
          navigate("/feedback", { state: cleanTicket });
        } else {
          console.warn("Full ticket not found, using minimal data");
          // Fallback: navigate with minimal ticket data if fetch fails
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = ticket;
          navigate("/feedback", { state: cleanTicket });
        }
      } catch (error) {
        console.error("Error fetching ticket:", error);
        // Fallback: navigate with minimal ticket data if fetch fails
        const {
          assignTimer,
          finalTimer,
          followUpTimer,
          initialTimer,
          timer,
          ...cleanTicket
        } = ticket;
        navigate("/feedback", { state: cleanTicket });
      }
    }
  };

  const TicketCountCell = ({ count, tickets, type, isLast = false, padding = 12 }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const handleTicketClickInCell = (ticket) => {
      handlePopoverClose();
      if (ticket && ticket._id) {
        // Remove timer-related fields before navigation
        const {
          assignTimer,
          finalTimer,
          followUpTimer,
          initialTimer,
          timer,
          ...cleanTicket
        } = ticket;
        // Ensure _id is properly formatted for navigation
        const ticketForNavigation = {
          ...cleanTicket,
          _id: ticket._id.toString ? ticket._id.toString() : ticket._id
        };
        console.log("Navigating with ticket:", ticketForNavigation);
        navigate("/feedback", { state: ticketForNavigation });
      }
    };

    if (!count || count === 0) {
      return (
        <td style={{
          fontFamily: "Inter", 
          borderRight: isLast ? "none" : "1px solid #f3f4f6", 
          border: "1px solid #e5e7eb", 
          padding: padding, 
          textAlign: "center", 
          color: "#6b7280" 
        }}>
          {count || 0}
        </td>
      );
    }

    return (
      <td style={{
        fontFamily: "Inter", 
        borderRight: isLast ? "none" : "1px solid #f3f4f6", 
        border: "1px solid #e5e7eb", 
        padding: padding, 
        textAlign: "center", 
        color: type === "closed" ? (count > 0 ? "#16a34a" : "#6b7280") : "#6b7280",
        cursor: "pointer",
        position: "relative"
      }}>
        <Box
          aria-owns={open ? "ticket-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={(e) => {
            console.log("Hovering over count:", count, "Tickets:", tickets);
            handlePopoverOpen(e);
          }}
          onMouseLeave={handlePopoverClose}
          sx={{
            display: "inline-block",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "#00675d"
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to first ticket if available
            if (tickets && tickets.length > 0) {
              handleTicketClickInCell(tickets[0]);
            }
          }}
        >
          {count}
        </Box>
        <Popover
          id="ticket-popover"
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiPopover-paper": {
              pointerEvents: "auto",
            },
          }}
          onMouseEnter={() => {
            // Keep popover open when hovering over it
            if (anchorEl) {
              setAnchorEl(anchorEl);
            }
          }}
          onMouseLeave={handlePopoverClose}
        >
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Ticket IDs (Click to open):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {tickets && tickets.length > 0 ? (
                tickets.map((ticket, idx) => {
                  const ticketId = ticket.ticketId || ticket._id;
                  if (!ticketId) return null;
                  return (
                    <Box
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTicketClickInCell(ticket);
                      }}
                      sx={{
                        fontSize: "11px",
                        padding: "4px 8px",
                        background: "#00675d",
                        color: "#fff",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          background: "#005248",
                          textDecoration: "underline"
                        }
                      }}
                    >
                      {ticketId}
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{ fontSize: "11px", color: "#999" }}>
                  No tickets available
                </Typography>
              )}
            </Box>
          </Box>
        </Popover>
      </td>
    );
  };

  // Process and sort ticket channel data
  const sortedTicketChannelData = useMemo(() => {
    if (!ticketChannelData || !Array.isArray(ticketChannelData)) {
      return [];
    }

    const copy = [...ticketChannelData];
    return copy.sort((a, b) => {
      const openWithoutA = a?.open?.without || 0;
      const closedWithoutA = a?.closed?.without || 0;
      const openWithoutB = b?.open?.without || 0;
      const closedWithoutB = b?.closed?.without || 0;
      const openWithinA = a?.open?.within || 0;
      const closedWithinA = a?.closed?.within || 0;
      const openWithinB = b?.open?.within || 0;
      const closedWithinB = b?.closed?.within || 0;
      const totalWithoutA = openWithoutA + closedWithoutA;
      const totalWithoutB = openWithoutB + closedWithoutB;

      let va = 0;
      let vb = 0;
      if (ticketChannelSortKey === "openWithout") { va = openWithoutA; vb = openWithoutB; }
      else if (ticketChannelSortKey === "closedWithout") { va = closedWithoutA; vb = closedWithoutB; }
      else if (ticketChannelSortKey === "openWithin") { va = openWithinA; vb = openWithinB; }
      else if (ticketChannelSortKey === "closedWithin") { va = closedWithinA; vb = closedWithinB; }
      else { va = totalWithoutA; vb = totalWithoutB; }
      return ticketChannelSortAsc ? va - vb : vb - va;
    });
  }, [ticketChannelData, ticketChannelSortKey, ticketChannelSortAsc]);

  const handleDateChange = (dates) => {
    if (dates) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleDepartmentDateChange = (dates) => {
    if (dates) {
      setDepartmentStartDate(dates[0]);
      setDepartmentEndDate(dates[1]);
    } else {
      setDepartmentStartDate(null);
      setDepartmentEndDate(null);
    }
  };

  const fetchReport = async (
    serviceFn,
    setData,
    setLoading,
    startDate,
    endDate
  ) => {
    setLoading(true);
    try {
      const response = await serviceFn({
        startDate: startDate.format("DD-MM-YYYY"),
        endDate: endDate.format("DD-MM-YYYY"),
      });
      // Debug logging for category report
      if (serviceFn === getCategoryReport) {
        console.log("Category Report API Response:", response);
        console.log("Category Report Data:", response.data);
      }
      setData(response.data || {});
    } catch (error) {
      console.error("Error fetching report:", error);
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(
      getTicketStatusReport,
      setTicketStatusData,
      setLoadingTicketStatus,
      startDate,
      endDate
    );
    fetchReport(
      getTicketPriorityReport,
      setTicketPriorityData,
      setLoadingTicketPriority,
      startDate,
      endDate
    );
    fetchReport(
      getTicketChannelReport,
      setTicketChannelData,
      setLoadingTicketChannel,
      startDate,
      endDate
    );
    fetchReport(
      getSlaReport,
      setSlaReportData,
      setLoadingSlaReport,
      startDate,
      endDate
    );
    fetchReport(
      getCategoryReport,
      setCategoryReportData,
      setLoadingCategoryReport,
      startDate,
      endDate
    );
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport(
      getDepartmentReport,
      (data) => setDepartmentReportData(Array.isArray(data) ? data : []),
      setLoadingDepartmentReport,
      departmentStartDate,
      departmentEndDate
    );
  }, [departmentStartDate, departmentEndDate]);

  const { departmentColumns, departmentPdfColumns, departmentTableData } =
    useMemo(() => {
      const departmentDateColumns = [];
      const departmentPdfDateColumns = [];
      if (departmentStartDate && departmentEndDate) {
        let currentDate = dayjs(departmentStartDate);
        while (
          currentDate.isBefore(departmentEndDate) ||
          currentDate.isSame(departmentEndDate)
        ) {
          departmentDateColumns.push({
            id: currentDate.format("DD-MM-YYYY"),
            label: currentDate.format("DD-MM-YYYY"), // Full date for web
            accessorKey: currentDate.format("DD-MM-YYYY"),
          });
          departmentPdfDateColumns.push({
            id: currentDate.format("DD-MM-YYYY"),
            label: currentDate.format("D"), // Day only for PDF
            accessorKey: currentDate.format("DD-MM-YYYY"),
          });
          currentDate = currentDate.add(1, "day");
        }
      }

      const departmentColumns = [
        { id: "description", label: "Description", accessorKey: "description" },
        ...departmentDateColumns,
        { id: "total", label: "Total", accessorKey: "total" },
      ];

      const departmentPdfColumns = [
        { id: "description", label: "Description", accessorKey: "description" },
        ...departmentPdfDateColumns,
        { id: "total", label: "Total", accessorKey: "total" },
      ];

      const statuses = [
        { key: "unassigned", label: "Unassigned" },
        { key: "assigned", label: "Assigned" },
        { key: "reassigned", label: "Reassigned" },
        { key: "completed", label: "Completed" },
      ];

      const departmentTableData = statuses.map((status) => {
        const row = { description: status.label };
        let total = 0;

        departmentDateColumns.forEach((col) => {
          const dateData = departmentReportData?.find(
            (d) => d._id === col.accessorKey
          );
          const value = dateData ? dateData[status.key] || 0 : 0;
          row[col.accessorKey] = value;
          total += value;
        });

        row.total = total;
        return row;
      });

      return { departmentColumns, departmentPdfColumns, departmentTableData };
    }, [departmentReportData, departmentStartDate, departmentEndDate]);

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    doc.text("Reports", 14, 16);
    let yPos = 22;

    // Ticket Status Report
    autoTable(doc, {
      startY: yPos,
      head: [["Ticket Status Report", "Value"]],
      body: [
        ["Total Assigned Cases", ticketStatusData.totalAssignedCases || 0],
        ["On Time Assigned Cases", ticketStatusData.onTimeAssignedCases || 0],
        [
          "Not On Time Assigned Cases",
          ticketStatusData.notOnTimeAssignedCases || 0,
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Ticket Priority Report
    autoTable(doc, {
      startY: yPos,
      head: [["Ticket Priority Report", "Total"]],
      body: ticketPriorityTableData.map((row) => [row.description, row.count]),
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // SLA Report
    autoTable(doc, {
      startY: yPos,
      head: [["SLA Report", "Within SLA", "Overdue KPI", "Total"]],
      body: [
        [
          "Open",
          slaReportData.open?.within ?? 0,
          slaReportData.open?.without ?? 0,
          (slaReportData.open?.within ?? 0) +
            (slaReportData.open?.without ?? 0),
        ],
        [
          "Closed",
          slaReportData.closed?.within ?? 0,
          slaReportData.closed?.without ?? 0,
          (slaReportData.closed?.within ?? 0) +
            (slaReportData.closed?.without ?? 0),
        ],
        [
          "Total",
          (slaReportData.open?.within ?? 0) +
            (slaReportData.closed?.within ?? 0),
          (slaReportData.open?.without ?? 0) +
            (slaReportData.closed?.without ?? 0),
          (slaReportData.open?.within ?? 0) +
            (slaReportData.open?.without ?? 0) +
            (slaReportData.closed?.within ?? 0) +
            (slaReportData.closed?.without ?? 0),
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Ticket Channel Report
    autoTable(doc, {
      startY: yPos,
      head: [
        [
          {
            content: "Ticket Channel Report",
            colSpan: 6,
            styles: { halign: "center", fillColor: [22, 160, 133] },
          },
        ],
        [
          { content: "Assigned To", rowSpan: 2 },
          { content: "Position", rowSpan: 2 },
          { content: "Open", colSpan: 2, styles: { halign: "center" } },
          { content: "Closed", colSpan: 2, styles: { halign: "center" } },
        ],
        ["Within", "Overdue KPI", "Within", "Overdue KPI"],
      ],
      body: ticketChannelData.map((row) => [
        row.assignedTo,
        row.position,
        row.open.within,
        row.open.without,
        row.closed.within,
        row.closed.without,
      ]),
      theme: "grid",
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Category Report
    const categoryRows =
      categoryReportData && typeof categoryReportData === "object"
        ? Object.entries(categoryReportData).map(([key, value]) => ({
            description: key,
            openWithin: value.open.within,
            openWithout: value.open.without,
            closedWithin: value.closed.within,
            closedWithout: value.closed.without,
            total:
              value.open.within +
              value.open.without +
              value.closed.within +
              value.closed.without,
          }))
        : [];

    autoTable(doc, {
      startY: yPos,
      head: [
        [
          {
            content: "Category Report",
            colSpan: 6,
            styles: { halign: "center", fillColor: [22, 160, 133] },
          },
        ],
        [
          { content: "Description", rowSpan: 2 },
          { content: "Open", colSpan: 2, styles: { halign: "center" } },
          { content: "Closed", colSpan: 2, styles: { halign: "center" } },
          { content: "Total", rowSpan: 2 },
        ],
        ["Within", "Overdue KPI", "Within", "Overdue KPI"],
      ],
      body: categoryRows.map((row) => [
        row.description,
        row.openWithin,
        row.openWithout,
        row.closedWithin,
        row.closedWithout,
        row.total,
      ]),
      theme: "grid",
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Department Report
    const departmentDateColumns = departmentPdfColumns.filter(
      (c) => c.id !== "description" && c.id !== "total"
    );
    const departmentTableBody = departmentTableData.map((row) => [
      row.description,
      ...departmentDateColumns.map((col) => row[col.accessorKey]),
      row.total,
    ]);

    if (departmentTableBody.length === 0) {
      departmentTableBody.push([
        {
          content: "No data available",
          colSpan: departmentDateColumns.length + 2,
          styles: { halign: "center" },
        },
      ]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [
        ["Description", ...departmentDateColumns.map((c) => c.label), "Total"],
      ],
      body: departmentTableBody,
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save("reports.pdf");
  };

  const renderTicketStatusCards = (data, loading) => {
    const colors = {
      totalAssignedCases: "#007bff",
      onTimeAssignedCases: "#28a745",
      notOnTimeAssignedCases: "#dc3545",
    };
    const icons = {
      totalAssignedCases: (
        <AssignmentTurnedIn
          fontSize="large"
          sx={{ color: colors.totalAssignedCases }}
        />
      ),
      onTimeAssignedCases: (
        <AccessTime
          fontSize="large"
          sx={{ color: colors.onTimeAssignedCases }}
        />
      ),
      notOnTimeAssignedCases: (
        <ErrorOutline
          fontSize="large"
          sx={{ color: colors.notOnTimeAssignedCases }}
        />
      ),
    };
    const cardData = [
      {
        title: "Total Assigned Cases",
        key: "totalAssignedCases",
        value: data?.totalAssignedCases || 0,
      },
      {
        title: "On Time Assigned Cases",
        key: "onTimeAssignedCases",
        value: data?.onTimeAssignedCases || 0,
      },
      {
        title: "Not On Time Assigned Cases",
        key: "notOnTimeAssignedCases",
        value: data?.notOnTimeAssignedCases || 0,
      },
    ];

    return (
      <Grid container spacing={2}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "120px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          cardData.map((item) => (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
              key={item.key}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": { transform: "scale(1.03)" },
                }}
              >
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}
                >
                  {icons[item.key]}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#666",
                        fontWeight: 500,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Inter, sans-serif",
                        color: colors[item.key],
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    );
  };

  const ticketPriorityTableData = ticketPriorityData
    ? [
        { description: "Assigned", count: ticketPriorityData.assignedCount },
        {
          description: "Reassigned",
          count: ticketPriorityData.reAssignedCount,
        },
        { description: "Completed", count: ticketPriorityData.completedCount },
        { description: "Reject", count: ticketPriorityData.rejectCount },
        {
          description: "Unassigned",
          count: ticketPriorityData.unassignedCount,
        },
      ]
    : [];

  const renderTicketChannelTable = (title, data, loading) => {
    const rowsToRender = sortedTicketChannelData;

    return (
      <Grid item xs={12} sm={12}>
        <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontFamily: "Inter, sans-serif" }}
          >
            {title}
          </Typography>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rowsToRender && rowsToRender.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th style={{ 
                    fontFamily: "Inter", 
                    fontSize: 12, 
                    fontWeight: 600, 
                    color: "#374151", 
                    letterSpacing: "0.6px", 
                    border: "1px solid #e5e7eb", 
                    padding: "8px 12px",
                    textAlign: "left"
                  }}>
                    ASSIGNED TO
                  </th>
                  <th style={{ 
                    fontFamily: "Inter", 
                    fontSize: 12, 
                    fontWeight: 600, 
                    color: "#374151", 
                    letterSpacing: "0.6px", 
                    border: "1px solid #e5e7eb", 
                    padding: "8px 12px",
                    textAlign: "left"
                  }}>
                    POSITION
                  </th>
                  <th
                    colSpan={2}
                    style={{
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px",
                      border: "1px solid #e5e7eb",
                      padding: "8px 12px",
                      textAlign: "center",
                    }}
                  >
                    OPEN
                  </th>
                  <th
                    colSpan={2}
                    style={{
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px",
                      border: "1px solid #e5e7eb",
                      padding: "8px 12px",
                      textAlign: "center",
                    }}
                  >
                    CLOSED
                  </th>
                </tr>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <th style={{ border: "1px solid #e5e7eb", padding: 6 }}></th>
                  <th style={{ border: "1px solid #e5e7eb", padding: 6 }}></th>
                  <th 
                    onClick={() => handleTicketChannelSort("openWithin")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 6, 
                      textAlign: "center", 
                      fontWeight: ticketChannelSortKey === "openWithin" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                  >
                    Within{ticketChannelSortKey === "openWithin" ? (ticketChannelSortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleTicketChannelSort("openWithout")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 6, 
                      textAlign: "center", 
                      fontWeight: ticketChannelSortKey === "openWithout" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                  >
                    Overdue KPI{ticketChannelSortKey === "openWithout" ? (ticketChannelSortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleTicketChannelSort("closedWithin")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 6, 
                      textAlign: "center", 
                      fontWeight: ticketChannelSortKey === "closedWithin" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                  >
                    Within{ticketChannelSortKey === "closedWithin" ? (ticketChannelSortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleTicketChannelSort("closedWithout")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 6, 
                      textAlign: "center", 
                      fontWeight: ticketChannelSortKey === "closedWithout" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                  >
                    Overdue KPI{ticketChannelSortKey === "closedWithout" ? (ticketChannelSortAsc ? " ▲" : " ▼") : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsToRender.map((row, index) => (
                  <tr 
                    key={index}
                    style={{ 
                      borderBottom: index === rowsToRender.length - 1 ? "none" : "1px solid #e5e7eb",
                      transition: "background-color 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }}
                  >
                    <td style={{ 
                      fontFamily: "Inter", 
                      fontSize: 14, 
                      color: "#111827", 
                      borderRight: "1px solid #f3f4f6", 
                      border: "1px solid #e5e7eb", 
                      padding: "10px 12px" 
                    }}>
                      {row.assignedTo}
                    </td>
                    <td style={{ 
                      fontFamily: "Inter", 
                      fontSize: 14, 
                      color: "#111827", 
                      borderRight: "1px solid #f3f4f6", 
                      border: "1px solid #e5e7eb", 
                      padding: "10px 12px" 
                    }}>
                      <span style={{
                        fontFamily: "Inter", 
                        fontSize: "0.75rem", 
                        padding: "2px 8px", 
                        borderRadius: 16, 
                        background: row.position === "N/A" ? "#f3f4f6" : "#dbeafe", 
                        color: row.position === "N/A" ? "#374151" : "#1e40af" 
                      }}>
                        {row.position}
                      </span>
                    </td>
                    <TicketCountCell 
                      count={row.open?.within || 0} 
                      tickets={row.open?.withinTicketIds || []} 
                      type="open"
                    />
                    <TicketCountCell 
                      count={row.open?.without || 0} 
                      tickets={row.open?.withoutTicketIds || []} 
                      type="open"
                    />
                    <TicketCountCell 
                      count={row.closed?.within || 0} 
                      tickets={row.closed?.withinTicketIds || []} 
                      type="closed"
                    />
                    <TicketCountCell 
                      count={row.closed?.without || 0} 
                      tickets={row.closed?.withoutTicketIds || []} 
                      type="closed"
                      isLast={true}
                    />
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Typography sx={{ mt: 2 }}>No data available.</Typography>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderGenericTable = (title, data, loading) => (
    <Grid item xs={12} sm={6} md={4} key={title}>
      <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontFamily: "Inter, sans-serif" }}
        >
          {title}
        </Typography>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : data && data.length > 0 ? (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Description
                </th>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {row.description}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {row.count}
                  </td>
                </tr>
              ))}
              <tr>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </td>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {data.reduce((sum, row) => sum + row.count, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <Typography sx={{ mt: 2 }}>No data available.</Typography>
        )}
      </Paper>
    </Grid>
  );

  const renderCategoryTable = (title, data, loading) => {
    const rows = categoryTableRows;

    // ✅ Calculate overall totals
    const totalOpenWithin = rows.reduce((sum, r) => sum + r.openWithin, 0);
    const totalOpenWithout = rows.reduce((sum, r) => sum + r.openWithout, 0);
    const totalClosedWithin = rows.reduce((sum, r) => sum + r.closedWithin, 0);
    const totalClosedWithout = rows.reduce(
      (sum, r) => sum + r.closedWithout,
      0
    );
    const totalOverall =
      totalOpenWithin +
      totalOpenWithout +
      totalClosedWithin +
      totalClosedWithout;

    return (
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontFamily: "Inter, sans-serif" }}
          >
            {title}
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows && rows.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th
                    rowSpan={2}
                    style={{ 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px", 
                      border: "1px solid #e5e7eb", 
                      padding: "8px 12px",
                      textAlign: "left"
                    }}
                  >
                    DESCRIPTION
                  </th>
                  <th
                    colSpan={2}
                    style={{ 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px", 
                      border: "1px solid #e5e7eb", 
                      padding: 8,
                      textAlign: "center"
                    }}
                  >
                    OPEN
                  </th>
                  <th
                    colSpan={2}
                    style={{ 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px", 
                      border: "1px solid #e5e7eb", 
                      padding: 8,
                      textAlign: "center"
                    }}
                  >
                    CLOSED
                  </th>
                  <th
                    rowSpan={2}
                    style={{ 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      fontWeight: 600, 
                      color: "#374151", 
                      letterSpacing: "0.6px", 
                      border: "1px solid #e5e7eb", 
                      padding: "8px 12px",
                      textAlign: "center"
                    }}
                  >
                    TOTAL
                  </th>
                </tr>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th 
                    onClick={() => handleCategorySort("openWithin")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 4, 
                      textAlign: "center", 
                      fontWeight: categorySortKey === "openWithin" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                  >
                    Within{categorySortKey === "openWithin" ? (categorySortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleCategorySort("openWithout")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 4, 
                      textAlign: "center", 
                      fontWeight: categorySortKey === "openWithout" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                  >
                    Overdue KPI{categorySortKey === "openWithout" ? (categorySortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleCategorySort("closedWithin")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 4, 
                      textAlign: "center", 
                      fontWeight: categorySortKey === "closedWithin" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                  >
                    Within{categorySortKey === "closedWithin" ? (categorySortAsc ? " ▲" : " ▼") : ""}
                  </th>
                  <th 
                    onClick={() => handleCategorySort("closedWithout")} 
                    style={{ 
                      cursor: "pointer", 
                      fontFamily: "Inter", 
                      fontSize: 12, 
                      color: "#4b5563", 
                      border: "1px solid #e5e7eb", 
                      padding: 4, 
                      textAlign: "center", 
                      fontWeight: categorySortKey === "closedWithout" ? 700 : 400,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                  >
                    Overdue KPI{categorySortKey === "closedWithout" ? (categorySortAsc ? " ▲" : " ▼") : ""}
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, index) => (
                  <tr 
                    key={index}
                    style={{ 
                      backgroundColor: "#fff", 
                      borderTop: "1px solid #e5e7eb",
                      transition: "background-color 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }}
                  >
                    <td style={{ 
                      fontFamily: "Inter", 
                      fontSize: 14, 
                      color: "#111827", 
                      lineHeight: "20px", 
                      borderRight: "1px solid #f3f4f6", 
                      border: "1px solid #e5e7eb", 
                      padding: index === 0 ? 8 : 12 
                    }}>
                      {row.description}
                    </td>
                    <TicketCountCell 
                      count={row.openWithin || 0} 
                      tickets={row.openWithinTickets || []} 
                      type="open"
                      padding={index === 0 ? 8 : 12}
                    />
                    <TicketCountCell 
                      count={row.openWithout || 0} 
                      tickets={row.openWithoutTickets || []} 
                      type="open"
                      padding={index === 0 ? 8 : 12}
                    />
                    <TicketCountCell 
                      count={row.closedWithin || 0} 
                      tickets={row.closedWithinTickets || []} 
                      type="closed"
                      padding={index === 0 ? 8 : 12}
                    />
                    <TicketCountCell 
                      count={row.closedWithout || 0} 
                      tickets={row.closedWithoutTickets || []} 
                      type="closed"
                      isLast={false}
                      padding={index === 0 ? 8 : 12}
                    />
                    <td style={{ 
                      textAlign: "center", 
                      fontFamily: "Inter", 
                      fontSize: 14, 
                      color: "#111827", 
                      border: "1px solid #e5e7eb", 
                      padding: index === 0 ? 8 : 12 
                    }}>
                      {row.total}
                    </td>
                  </tr>
                ))}

                {/* ✅ Total Row */}
                <tr style={{ fontWeight: "bold", backgroundColor: "#f9fafb" }}>
                  <td style={{ 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    Total
                  </td>
                  <td style={{ 
                    textAlign: "center", 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    {totalOpenWithin}
                  </td>
                  <td style={{ 
                    textAlign: "center", 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    {totalOpenWithout}
                  </td>
                  <td style={{ 
                    textAlign: "center", 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    {totalClosedWithin}
                  </td>
                  <td style={{ 
                    textAlign: "center", 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    {totalClosedWithout}
                  </td>
                  <td style={{ 
                    textAlign: "center", 
                    fontFamily: "Inter", 
                    fontSize: 14, 
                    color: "#111827", 
                    border: "1px solid #e5e7eb", 
                    padding: 8 
                  }}>
                    {totalOverall}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <Typography sx={{ mt: 2 }}>No data available.</Typography>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderSlaTable = (title, data, loading) => (
    <Grid item xs={12} sm={6} md={4} key={title}>
      <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontFamily: "Inter, sans-serif" }}
        >
          {title}
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : data && (data.open || data.closed) ? (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Description
                </th>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Within SLA
                </th>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Overdue KPI
                </th>
                <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Open
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {data.open?.within ?? 0}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {data.open?.without ?? 0}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {(data.open?.within ?? 0) + (data.open?.without ?? 0)}
                </td>
              </tr>

              <tr>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Closed
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {data.closed?.within ?? 0}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {data.closed?.without ?? 0}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {(data.closed?.within ?? 0) + (data.closed?.without ?? 0)}
                </td>
              </tr>

              <tr style={{ fontWeight: "bold", backgroundColor: "#fafafa" }}>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  Total
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {(data.open?.within ?? 0) + (data.closed?.within ?? 0)}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {(data.open?.without ?? 0) + (data.closed?.without ?? 0)}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                  {(data.open?.within ?? 0) +
                    (data.open?.without ?? 0) +
                    (data.closed?.within ?? 0) +
                    (data.closed?.without ?? 0)}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <Typography sx={{ mt: 2 }}>No data available.</Typography>
        )}
      </Paper>
    </Grid>
  );

  return (
    <div className="mainContent">
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <RangePicker
          value={[startDate, endDate]}
          onChange={handleDateChange}
          style={{ height: "38px" }}
        />
        <Button
          className="addButton"
          variant="contained"
          onClick={handleDownloadPdf}
        >
          Download PDF
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{ fontFamily: "Inter, sans-serif" }}
          gutterBottom
        >
          Ticket Status Report
        </Typography>
        {renderTicketStatusCards(ticketStatusData, loadingTicketStatus)}
      </Box>

      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          {renderGenericTable(
            "Ticket Priority Report",
            ticketPriorityTableData,
            loadingTicketPriority
          )}
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          {renderSlaTable("SLA Report", slaReportData, loadingSlaReport)}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          {renderTicketChannelTable(
            "Ticket Channel Report",
            ticketChannelData,
            loadingTicketChannel
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          {renderCategoryTable(
            "Category Report",
            categoryReportData,
            loadingCategoryReport
          )}
        </Grid>
      </Grid>

      <Box sx={{ mb: 4, mt: 4 }}>
        <Stack mb={2} width={"fit-content"}>
          <DepartmentDatePicker
            value={[departmentStartDate, departmentEndDate]}
            onChange={handleDepartmentDateChange}
          />
        </Stack>

        <DepartmentReport
          data={departmentTableData}
          columns={departmentColumns}
          loading={loadingDepartmentReport}
        />
      </Box>
    </div>
  );
};

export default ReportCategory;
